import { BatchManager } from 'common/frontend/firebase/firestore';
import moment from 'moment-timezone';
import {
	OrderProduct,
	Shopper,
	ResponsiblePerson,
	ResponsiblePersonDetails,
	ProductApi,
	OrderInfo,
	OrderObject,
} from 'common/types';
import { Location as ShopLocation, PaymentMethod } from 'common/types/common';
import errorHandler from 'common/services/errorHandler';
import i18n from 'services/localization/i18n';
import { WriteBatch } from 'common/frontend/firebase/firestore';
import { api } from 'common/frontend/api';
import { newFirestoreId } from 'common/utils/dataMigrations';

export const getProductCodes = (products: OrderProduct[]) => {
	const productCodes = [];
	for (const product of products) {
		if (product.productCode) {
			productCodes.push(product.productCode);
		}
		if (product.setProducts) {
			for (const setProduct of product.setProducts) {
				if (setProduct.productCode) {
					productCodes.push(setProduct.productCode);
				}
			}
		}
	}
	return productCodes.filter((code) => code !== '');
};

export const deleteProductsFromDb = (batch: WriteBatch | null, productIdsToDelete: string[]) => {
	let promises = [];
	const apiRef = api(batch ?? undefined);
	for (const productId of productIdsToDelete) {
		const p = apiRef.orderProducts.doc(productId).delete();
		promises.push(p);
	}
	return !batch ? promises : undefined;
};

export const deleteShoppersFromDb = (batch: WriteBatch | null, shopperIdsToDelete: string[]) => {
	let promises = [];
	const apiRef = api(batch ?? undefined);
	for (const shopperId of shopperIdsToDelete) {
		const p = apiRef.orderShoppers.doc(shopperId).delete();
		promises.push(p);
	}
	return !batch ? promises : undefined;
};

export const updateActiveRentalToDb = async (rental: OrderObject) => {
	const { rentalInfo, shoppers, products } = rental;
	const rentalId = rentalInfo.id;
	try {
		const batchManager = new BatchManager();
		const productCodes = [];
		for (const product of products) {
			if (product.productCode) {
				productCodes.push(product.productCode);
			}
			if (product.setProducts) {
				for (const setProduct of product.setProducts) {
					if (setProduct.productCode) {
						productCodes.push(setProduct.productCode);
					}
				}
			}
		}
		api(batchManager.batch())
			.orders.doc(rentalId)
			.update({
				...rentalInfo,
				rentalProductCodes: productCodes,
			});
		for (const shopper of shoppers) {
			api(batchManager.batch()).orderShoppers.doc(shopper.id).set(shopper, { merge: true });
		}
		for (const product of products) {
			api(batchManager.batch()).orderProducts.doc(product.id).set(product, { merge: true });
		}
		await batchManager.commit();
	} catch (e) {
		errorHandler.report(e);
	}
};

export function hasLateFees(productObject: OrderProduct | OrderProduct[]) {
	if (productObject instanceof Array) {
		for (const product of productObject) {
			if (product.terms && (product.terms.lostOrDamaged || product.terms.lateReturn)) {
				return true;
			}
		}
	} else {
		if (
			productObject.terms &&
			(productObject.terms.lostOrDamaged || productObject.terms.lateReturn)
		) {
			return true;
		}
	}
	return false;
}

export const getTotalLiability = (products: OrderProduct[]) => {
	let totalLiability = 0;
	products.forEach((item) => {
		totalLiability =
			item.terms && item.terms.lostOrDamaged && item.terms.lostOrDamaged.maxCompensation
				? totalLiability + item.terms.lostOrDamaged.maxCompensation
				: totalLiability;
	});
	return totalLiability;
};

export function throwLogicError() {
	// tslint:disable-next-line
	// eslint-disable-next-line no-throw-literal
	throw 'Logical error, should not happen';
}

export function makeSwitchExhaustive() {
	// tslint:disable-next-line
	// eslint-disable-next-line no-throw-literal
	throw 'This should never happen';
}

export const getRandomUserId = (): string => {
	return newFirestoreId();
};

export function generateUUID(): string {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		var r = (Math.random() * 16) | 0,
			v = c == 'x' ? r : (r & 0x3) | 0x8; // eslint-disable-line
		return v.toString(16);
	});
}

export const pageVisibilityApi = () => {
	let hidden: string = '';
	let visibilityChange: string = '';
	if (typeof document.hidden !== 'undefined') {
		// Opera 12.10 and Firefox 18 and later support
		hidden = 'hidden';
		visibilityChange = 'visibilitychange';
	} else if (typeof document['msHidden'] !== 'undefined') {
		hidden = 'msHidden';
		visibilityChange = 'msvisibilitychange';
	} else if (typeof document['webkitHidden'] !== 'undefined') {
		hidden = 'webkitHidden';
		visibilityChange = 'webkitvisibilitychange';
	}

	return { hidden, visibilityChange };
};

export const filterRentalsByLocation = (
	rentals: OrderInfo[],
	selectedLocationId: string,
	allLocations: ShopLocation[],
	type: 'start' | 'end' | 'both',
) => {
	const selectedLocation = allLocations.find((location) => location.id === selectedLocationId);

	if (!selectedLocation) {
		return [];
	}

	return rentals.filter((rental) =>
		type === 'start'
			? selectedLocationId === rental.startLocation.id ||
			  selectedLocation.address === rental.startLocation.address
			: type === 'end'
			? selectedLocationId === rental.endLocation.id ||
			  selectedLocation.address === rental.endLocation.address
			: selectedLocationId === rental.startLocation.id ||
			  selectedLocationId === rental.endLocation.id ||
			  selectedLocation.address === rental.startLocation.address ||
			  selectedLocation.address === rental.endLocation.address,
	);
};

export const getTimeNowAsISOString = () => {
	const timeNow = moment().startOf('minute').toISOString();
	return timeNow;
};

export const getResponsiblePersonDetails = (
	responsiblePerson: ResponsiblePerson,
	shoppers: Shopper[],
): ResponsiblePersonDetails | null => {
	if (responsiblePerson.external) {
		return responsiblePerson.person;
	}
	return shoppers.filter((s) => s.id === responsiblePerson.shopperId)[0] || null;
};

export const updateVats = (
	products: ProductApi[],
	oldVat: number,
	newVat: number,
): ProductApi[] => {
	return products
		.filter((product) => product.vatPercent === oldVat)
		.map((product) => {
			return {
				...product,
				vatPercent: newVat,
			};
		});
};

export const getDefaultPaymentOptions = (): PaymentMethod[] => {
	return ['CARD_TERMINAL', 'CASH', 'INVOICE'];
};

export const validatePassword = (password: string): string | undefined => {
	if (!validPassword(password)) {
		return i18n.t('common:validation.lengthAtLeastIncludeLettersNumbers', {
			field: i18n.t('common:form.password'),
			int: '7',
		});
	}
	return;
};

export const validPassword = (password: string) => {
	const invalidPwValidationRegexp = /^(.{0,6}|[^0-9]*|[^a-zA-Z]*)$/; // Min. 7 chars, both letters and numbers
	return !invalidPwValidationRegexp.test(password);
};

export const getRentalLinkUrl = (linkId: string, lang: string): string => {
	const env = process.env.REACT_APP_ENV;
	const linkBaseUrl = getLinkBaseUrl(env);
	const langParam = lang ? `?lang=${lang}` : '';
	return `${linkBaseUrl}/${linkId}${langParam}`;
};

export const getLinkBaseUrl = (env: string = 'production') => {
	switch (env) {
		case 'local':
			return `http://localhost:3000`;
		case 'development':
			return `https://dev.link.rentle.shop`;
		case 'production':
		default:
			return `https://link.rentle.shop`;
	}
};
